import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ChevronLeft, Mail, Leaf } from "lucide-react";

function Sparkline({ data, t }) {
  const max = Math.max(1, ...data.map((v) => Number(v) || 0));
  return (
    <div className="rounded-2xl border border-border bg-primary/5 p-4">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-primary">{t("digest_trend_4w")}</p>
      <div className="flex items-end justify-between gap-2">
        {data.map((v, i) => (
          <div key={i} className="flex flex-1 flex-col items-center gap-1">
            <span className="w-full rounded-t bg-primary" style={{ height: Math.max(4, (Number(v) || 0) / max * 48) }} />
            <span className="text-[9px] text-muted-foreground">W{i + 1}</span>
            <span className="text-[11px] font-semibold text-primary">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function WeeklyDigest() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("weekly_digest"));
  const [data, setData] = useState(undefined);
  const [sending, setSending] = useState(false);
  const [emailOn, setEmailOn] = useState(true);

  const load = useCallback(async () => {
    try {
      const [{ data: d }, prefs] = await Promise.all([
        api.get("/weekly-report/preview"),
        api.get("/weekly-digest/prefs").then((r) => r.data?.prefs || r.data).catch(() => null),
      ]);
      setData(d?.stats || d);
      if (prefs) setEmailOn(prefs.enabled !== false);
    } catch { setData(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendMe = async () => {
    if (sending) return;
    setSending(true);
    try { await api.post("/weekly-report/send-me"); toast.success(t("digest_sent_title")); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const toggleEmail = async (v) => {
    setEmailOn(v);
    try { await api.post("/weekly-digest/prefs", { enabled: v }); }
    catch (e) { toast.error(apiError(e)); setEmailOn(!v); }
  };

  const trend = Array.isArray(data?.trend_4w) ? data.trend_4w.map((w) => Number(w?.sessions_count || 0)) : [];
  const hasData = data && data.sessions_count;

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="weekly-digest-screen">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/account")} data-testid="digest-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <h1 className="font-display text-2xl text-foreground">{t("weekly_digest")}</h1>
      </div>

      {data === undefined ? <Spinner label={t("common.loading")} /> : (
        <div className="space-y-4">
          <div className="rounded-2xl border border-primary/40 bg-primary/5 p-5">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-primary">{t("this_week")}</p>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><p className="font-display text-2xl text-primary">{data?.sessions_count || 0}</p><p className="text-[11px] text-muted-foreground">{t("sessions")}</p></div>
              <div><p className="font-display text-2xl text-primary">{data?.current_streak || 0}</p><p className="text-[11px] text-muted-foreground">{t("streak_days")}</p></div>
              <div><p className="font-display text-2xl text-primary">-{data?.avg_intensity_drop || 0}</p><p className="text-[11px] text-muted-foreground">{t("avg_drop")}</p></div>
            </div>
          </div>

          {trend.some((v) => v > 0) ? <Sparkline data={trend} t={t} /> : null}

          {hasData ? (
            <div className="rounded-2xl border border-border bg-card p-4">
              {[["digest_unique_days", `${data.unique_days_practiced || 0} / 7`], ["digest_avg_duration", `${Math.round((data.avg_duration_seconds || 0) / 60)} min`], ["digest_biggest_drop", `-${data.biggest_drop || 0}`], ["digest_top_emotion", data.top_emotion], ["digest_top_technique", data.top_technique]].filter(([, v]) => v != null && v !== "").map(([lbl, v]) => (
                <div key={lbl} className="flex items-center justify-between border-b border-border/60 py-2.5 last:border-0">
                  <span className="text-sm text-muted-foreground">{t(lbl)}</span>
                  <span className="text-sm font-semibold text-foreground">{v}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-card p-8 text-center" data-testid="digest-empty">
              <Leaf className="h-7 w-7 text-muted-foreground" />
              <p className="text-sm italic text-muted-foreground">{t("digest_empty_client")}</p>
            </div>
          )}

          <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground">{t("weekly_digest_email_toggle")}</p>
              <p className="text-xs text-muted-foreground">{t("digest_hint_client")}</p>
            </div>
            <Switch checked={emailOn} onCheckedChange={toggleEmail} data-testid="digest-email-toggle" />
          </div>

          <button onClick={sendMe} disabled={sending} data-testid="digest-send-me" className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-foreground disabled:opacity-50">
            <Mail className="h-4 w-4" /> {t("digest_email_me")}
          </button>
        </div>
      )}
    </div>
  );
}
